import { Multirange, Range } from '@attain-apps/range'
import type { readableNumber } from '@attain-apps/string-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import type { WithInfo } from '../info'
import { Raw } from '../raw'

import { Enum } from './enum'
import { Structure } from './structure'
import type { Type } from './type'

/**
 * One of our core data types. All {@link Type} can be simplified into a
 * {@link Type.Intrinsic}
 */
export enum Intrinsic {
  // TODO: maybe get rid of this as intrinsic, and instead make this a
  // predefined structure. I.e. zones start with defined structures, and more
  // can be added. Essentially, new model type with 'struct::!contact' and
  // 'struct::!address'
  // Address = 'ADDRESS', // Street, city, state/region, postal code, country
  Boolean = 'BOOL',
  // Contact = 'CONTACT', // Name, email, phone number
  Currency = 'CURRENCY',
  Date = 'DATE',
  DateMultirange = 'DATE_MULTIRANGE',
  DateRange = 'DATE_RANGE',
  Decimal = 'DECIMAL',
  Integer = 'INT',
  List = 'LIST',
  Object = 'OBJECT',
  Percent = 'PERCENT',
  Text = 'TEXT',
  Url = 'URL', // e.g., https://... or attain://...
}
// Needs to be before namespace is defined
const TYPES: ReadonlySet<Intrinsic> = new Set(
  Object.values(Intrinsic) as Intrinsic[],
)
export namespace Intrinsic {
  export const ALL: readonly Intrinsic[] = Array.from(TYPES)
  export const isIntrinsic: TypeChecker<Intrinsic> = TypeChecker.Factory.oneOf(
    TYPES,
  ).usingAlias('common.value.types.Intrinsic')
  export const assertIsIntrinsic: TypeAsserter<Intrinsic> =
    TypeAsserter.Factory.from(isIntrinsic)

  /** Used to distribute over {@link Intrinsic} */
  type OfHelper<TRaw, TType extends Intrinsic = Intrinsic> = TType extends any
    ? TRaw extends Raw<TType>
      ? TType
      : never
    : never
  /**
   * {@link Intrinsic} for a given {@link Raw} value
   */
  export type Of<TRaw> =
    | OfHelper<TRaw>
    // Anything that is not a primitive and not a list can probably be an object
    | (TRaw extends Primitive | readonly unknown[] ? never : Intrinsic.Object)

  /**
   * Produces the {@link Intrinsic} corresponding to the given {@link Type}
   */
  export type From<TType extends Type> = TType extends Intrinsic
    ? TType
    : TType extends Enum
      ? Intrinsic.Text | Intrinsic.List
      : TType extends Structure<infer T>
        ? T extends readonly unknown[]
          ? Intrinsic.List
          : Intrinsic.List | Intrinsic.Object
        : never

  /**
   * Returns the {@link Intrinsic} of the given {@link Value}
   */
  export function from(source: Nullish): undefined
  export function from<const TType extends Type>(type: TType): From<TType>
  export function from<const TType extends Type>(
    type: TType | Nullish,
  ): From<TType> | undefined
  export function from<const TValue extends WithInfo>(
    val: TValue,
  ): TValue extends WithInfo<infer TType> ? From<TType> : never
  export function from<const TValue extends WithInfo>(
    val: TValue | Nullish,
  ): TValue extends WithInfo<infer TType> ? From<TType> | undefined : never
  export function from(
    source: Type | WithInfo | Nullish,
  ): Intrinsic | undefined {
    if (TypeChecker.isNullish(source)) {
      return undefined
    }
    if (isIntrinsic(source)) {
      // If it is already an intrinsic, return it
      return source
    }
    if (Enum.isEnum(source)) {
      // If it is an enum, return list for multiselects and text for single
      // selects
      return source.isMultiselect ? Intrinsic.List : Intrinsic.Text
    }
    if (Structure.isStructure(source)) {
      // If it is a structure, return the underlying intrinsic
      return source.intrinsic
    }
    if (source.type) {
      // If it is a value with info that has a defined type, return the
      // intrinsic for that type
      return from(source.type)
    }
    // Otherwise, try to infer the intrinsic for the given raw value
    return inferFrom(source.raw)
  }

  /**
   * Infers the {@link Intrinsic} of a given {@link Raw} value
   */
  export function inferFrom<TRaw extends Raw>(val: TRaw): Of<TRaw>
  export function inferFrom<TRaw extends Raw>(
    val: TRaw | Nullish,
  ): Of<TRaw> | undefined
  export function inferFrom(val: unknown): Intrinsic | undefined
  export function inferFrom(val: unknown): Intrinsic | undefined {
    if (TypeChecker.isNullish(val)) {
      return undefined
    }
    switch (typeof val) {
      case 'boolean':
        return Intrinsic.Boolean
      case 'number':
        return Intrinsic.Decimal
      case 'string':
        return Intrinsic.Text
      case 'object':
        if (TypeChecker.isReadonlyArray(val)) {
          return Intrinsic.List
        } else if (TypeChecker.isDate(val)) {
          return Intrinsic.Date
        } else if (val instanceof Range) {
          // TODO: is it always a DATE range?
          return Intrinsic.DateRange
        } else if (val instanceof Multirange) {
          // TODO: is it always a DATE multirange?
          return Intrinsic.DateMultirange
        }
        return Intrinsic.Object
      default:
        // NOTE: there is not a very helpful user-friendly error to throw
        // here, so we throw a regular error and if it needs to be
        // user-friendly, it will be converted to an UnexpectedError upstream
        throw new Error(
          `Cannot determine a valid type for value: ${format(val)}`,
        )
    }
  }

  /**
   * All valid {@link Intrinsic} types for the given {@link Raw} value
   */
  export function validFor<TRaw extends Raw>(val: TRaw): Of<TRaw>[]
  export function validFor(val: Raw): Intrinsic[] {
    return ALL.filter(type => Raw.getTypeCheckerFor(type)(val))
  }

  /**
   * Any {@link Intrinsic} that accepts numbers as a {@link Value.raw}
   */
  export type ForNumber = Intrinsic.Of<number>
  export namespace ForNumber {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export const ALL: readonly ForNumber[] = Object.values({
      [Intrinsic.Currency]: Intrinsic.Currency,
      [Intrinsic.Decimal]: Intrinsic.Decimal,
      [Intrinsic.Percent]: Intrinsic.Percent,
      [Intrinsic.Integer]: Intrinsic.Integer,
    } satisfies { [K in ForNumber]: K })
    export const isForNumber: TypeChecker<ForNumber> =
      TypeChecker.Factory.oneOf(ALL).usingAlias(
        'common.value.types.Intrinsic.ForNumber',
      )
    export const assertIsForNumber: TypeAsserter<ForNumber> =
      TypeAsserter.Factory.from(isForNumber)

    /**
     * Returns a {@link readableNumber.Preset} that best fits the given
     * {@link ForNumber type}
     */
    export function toReadableNumberPreset(
      type: ForNumber,
    ): readableNumber.Preset {
      switch (type) {
        case Intrinsic.Integer:
          return '#'
        case Intrinsic.Currency:
          return '$'
        case Intrinsic.Percent:
          return '%'
        case Intrinsic.Decimal:
          return '#.'
      }
    }
  }

  /**
   * Any {@link Intrinsic} that accepts strings as a {@link Value.raw}
   */
  export type ForString = Intrinsic.Of<string>
  export namespace ForString {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export const ALL: readonly ForString[] = Object.values({
      [Intrinsic.Text]: Intrinsic.Text,
      [Intrinsic.Url]: Intrinsic.Url,
    } satisfies { [K in ForString]: K })
    export const isForString: TypeChecker<ForString> =
      TypeChecker.Factory.oneOf(ALL).usingAlias(
        'common.value.types.Intrinsic.ForString',
      )
    export const assertIsForString: TypeAsserter<ForString> =
      TypeAsserter.Factory.from(isForString)
  }

  const GENERALIZED_LOOKUP = {
    [Intrinsic.Boolean]: Intrinsic.Boolean,
    [Intrinsic.Currency]: Intrinsic.Decimal,
    [Intrinsic.Date]: Intrinsic.Date,
    [Intrinsic.DateMultirange]: Intrinsic.DateMultirange,
    [Intrinsic.DateRange]: Intrinsic.DateRange,
    [Intrinsic.Decimal]: Intrinsic.Decimal,
    [Intrinsic.Integer]: Intrinsic.Decimal,
    [Intrinsic.List]: Intrinsic.List,
    [Intrinsic.Object]: Intrinsic.Object,
    [Intrinsic.Percent]: Intrinsic.Decimal,
    [Intrinsic.Text]: Intrinsic.Text,
    [Intrinsic.Url]: Intrinsic.Text,
  } as const satisfies Record<Intrinsic, Intrinsic>
  export type Generalized<TType extends Type = Type> =
    (typeof GENERALIZED_LOOKUP)[From<TType>]
  export namespace Generalized {
    // TODO: overload should no longer be needed when this is addressed:
    // https://github.com/microsoft/TypeScript/issues/33014
    /**
     * Returns {@link Generalized} version of given {@link Type}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function from<TType extends Type>(type: TType): Generalized<TType>
    export function from<TType extends Type>(
      type: TType | undefined,
    ): Generalized<TType> | undefined
    /**
     * If all given {@link Type} have the same {@link Generalized} type,
     * then that type is returned. Otherwise, `undefined` is returned
     */
    export function from<TTypes extends readonly Type[]>(
      ...types: TTypes
    ):
      | (TTypes extends readonly (infer TType extends Type)[]
          ? Generalized<TType>
          : never)
      | undefined
    export function from(
      ...types: readonly (Type | undefined)[]
    ): Generalized | undefined {
      const generalizedTypes = Array.from(
        new Set(
          types
            .map(type => {
              const intrinsic = Intrinsic.from(type)
              return intrinsic === undefined
                ? undefined
                : GENERALIZED_LOOKUP[intrinsic]
            })
            // If the type is unknown, we ignore it for the purpose of
            // generalizing. This could have the unfortunate consequence of
            // leading to incorrect generalizations, but it does mean we have
            // more successes in most happy path cases (e.g. where we don't know
            // the type, but it is in fact consistent with the other types)
            .filter(TypeChecker.isNotNullish.raw),
        ),
      )
      return generalizedTypes.length === 1 ? generalizedTypes[0] : undefined
    }
  }
}
